<template>
  <div>
    <template v-if="isLoading && !flowDlibr.currentPositionScroll">
      <b-row>
        <b-col class="text-center my-5" cols="12">
          <UnitLoading />
        </b-col>
      </b-row>
    </template>
    <template>
      <div :class="classObject">
        <b-card
          bg-variant="primary"
          text-variant="white"
          class="text-center mb-2 card-radius title-dlibr"
          v-if="
            !Boolean(comments.filters.flagged) &&
              !routeIsAnswer &&
              !isLoading &&
              comments.data.to < comments.data.total
          "
        >
          <b-card-text>
            {{ dlibr.title }} - {{ dlibr.description }}
          </b-card-text>
        </b-card>
        <b-row>
          <b-col class="my-5 text-center" cols="12" v-if="isEmpty">
            <UnitEmpty />
          </b-col>
        </b-row>
        <IntersectionObserver
          v-if="
            showLoadPrevious && comments.data.to < comments.data.total
          "
          @intersect-enter="handleEnter"
          @intersect-leave="handleLeave"
          :threshold="[1]"
        >
          <b-row>
            <b-col class="text-center py-3" cols="12">
              <b-spinner></b-spinner>
            </b-col>
          </b-row>
        </IntersectionObserver>
        <b-row
          v-for="c in comments.data.data"
          :key="c.id"
          class="no-gutters"
          :id="`comment${c.id}`"
        >
          <b-col cols="10" :offset="typeMap[c.type].offset">
            <Unit :comment="c" />
          </b-col>
          <b-col
            cols="12"
            :class="{
              'first-message': !Boolean(index),
              'mb-3': index === c.messages.length - 1
            }"
            :v-if="Boolean(c.messages.length)"
            v-for="(m, index) in c.messages"
            :key="m.id"
            :id="`msg${c.id}-${m.id}`"
          >
            <UnitMessage
              :comment="c"
              :message="m"
              :totalMessges="c.messages.length"
            />
          </b-col>
        </b-row>
      </div>
    </template>
    <PropertyPanel />
  </div>
</template>

<script>
/**
 * @file       Deliberation by url component.
 * @author     Appwapp <info@appwapp.com>
 * @copyright  © Centre collégial de développement de matériel didactique, Collège de Maisonneuve, 2021
 * @license    https://www.ccdmd.qc.ca
 * @version    1.0
 */

import store from "@/store";
import { commentsMixin } from "@/modules/comments/mixins.js";
import Unit from "@/modules/comments/components/unit.vue";
import UnitLoading from "@/modules/comments/components/unitLoading.vue";
import UnitEmpty from "@/modules/comments/components/unitEmpty.vue";
import PropertyPanel from "@/modules/deliberations/components/propertyPanel.vue";
import UnitMessage from "@/modules/comments/components/unitMessage.vue";
import IntersectionObserver from "@/modules/comments/components/intersectionObserver.vue";

/**
 * Export the component.
 * 
 * @type  {Object}
 */
export default {
  name: "deliberationByUrl",
  mixins: [commentsMixin],
  components: {
    UnitLoading,
    UnitEmpty,
    Unit,
    PropertyPanel,
    UnitMessage,
    IntersectionObserver
  },

  /**
   * Data used in the component.
   *
   * @return  {Object}
   */
  data() {
    return {
      showLoadPrevious: false
    };
  },

  /**
   * Before route change.
   * 
   * @param  {string}   to     Destination
   * @param  {string}   from   Location
   * @param  {Object}   next   The action
   */
  beforeRouteEnter(to, from, next) {
    if (!store.state.comments.flowDlibr.goToComment.id) {
      store
        .dispatch("deliberations/getDlibr", {
          params: { teacher: to.params.teacher, url: to.params.url }
        })
        .then(({ data }) =>
          next(vm => {
            if (from.name == "auth.login") {
              vm.$root.$bvToast.toast("Bienvenue à Dlibr", {
                title: "Connexion réussie"
              });
            }
            store.commit("comments/setFlowDlibr", {
              reset: true,
              fields: { goLastComment: true }
            });
            if (to.name == "deliberation.byUrl") {
              store.commit("comments/setCommentsFilters", {
                reset: true,
                fields:
                  from.query && from.query.deliberation
                    ? from.query
                    : { deliberation: data.data.find(d => d).id }
              });
            } else {
              store.commit("comments/setCommentsFilters", {
                reset: true,
                fields: {
                  deliberation: data.data.find(d => d).id,
                  root: to.params.id
                }
              });
            }
            store.dispatch("comments/getComments");
          })
        )
        .catch(() => {
          next(vm => {
            let msg = "Aucune délibération trouvée pour cette URL";
            if (
              from.name == "student.own.deliberations" ||
              from.name == "auth.login"
            ) {
              msg = "Vous n'êtes pas autorisé à accéder à cette délibération";
            }
            vm.$root.$bvToast.toast(msg, {
              title: "Erreur",
              variant: "danger"
            });
            vm.$router.back();
          });
        });
    } else {
      next();
    }
  },
  updated: function() {
    this.$nextTick(function() {
      if (this.flowDlibr.goLastComment && this.comments.status == "success") {
        this.$store.commit("comments/setFlowDlibr", {
          reset: true
        });
        window.scrollTo(0, 99999999);
      }
    });
  },
  mounted() {
    if (this.flowDlibr.goToComment.id) {
      store.commit("comments/setCommentsFilters", {
        reset: true,
        fields: {
          deliberation: this.dlibr.id,
          resourcesPerPage: this.flowDlibr.goToComment.resourcesPerPage,
          root: this.$route.params.id
        }
      });
      store.dispatch("comments/getComments").then(() => {
        this.goToRefElement(`#comment${this.flowDlibr.goToComment.id}`).then(
          res => {
            this.showLoadPrevious = res;
            store.commit("comments/setFlowDlibr", {
              reset: true
            });
          }
        );
      });
    } else {
      this.showLoadPrevious = true;
    }
    if (!this.isActiveJobs) {
      this.onActiveJobs();
    }
  },

  /**
   * Methods.
   * 
   * @type  {Object}
   */
  methods: {
    handleEnter() {
      this.loadPrevious(this.comments.data.data[0].id);
    },
    handleLeave() {}
  },

  /**
   * Computed properties.
   * 
   * @type  {Object}
   */
  computed: {
    classObject: function() {
      return {
        "scroll-trigger":
          this.isStudent && !this.isFilter && !this.routeIsAnswer,
        "scroll-trigger-filter":
          this.isStudent && this.isFilter && !this.routeIsAnswer,
        "scroll-trigger-answer":
          this.isStudent && this.routeIsAnswer && !this.isFilter,
        "scroll-trigger-answer-filter":
          this.isStudent && this.routeIsAnswer && this.isFilter,
        "scroll-trigger-teacher": this.isTeacher && !this.routeIsAnswer,
        "scroll-trigger-filter-teacher":
          this.isTeacher && this.isFilter && !this.routeIsAnswer,
        "scroll-trigger-answer-teacher":
          this.isTeacher && this.routeIsAnswer && !this.isFilter,
        "scroll-trigger-answer-filter-teacher":
          this.isTeacher && this.routeIsAnswer && this.isFilter
      };
    }
  }
};
</script>
<style lang="scss" scoped>
.card-radius {
  border-radius: 15px 15px 15px 15px !important;
}

.title-dlibr {
  margin-top: 11rem !important;
}

.scroll-trigger {
  margin-top: -100px !important;
}
.scroll-trigger-filter {
  margin-top: -60px !important;
}
.scroll-trigger-answer {
  margin-top: -40px !important;
}
.scroll-trigger-answer-filter {
  margin-top: 150px !important;
}
.scroll-trigger-teacher {
  margin-top: -60px !important;
}
.scroll-trigger-filter-teacher {
  margin-top: -20px !important;
}
.scroll-trigger-answer-teacher {
  margin-top: 140px !important;
}
.scroll-trigger-answer-filter-teacher {
  margin-top: 200px !important;
}

.first-message {
  margin-top: -1rem;
}

hr.bg-primary {
  height: 3px;
  border: none;
}
</style>
